import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { updateSparepartExpense, getSparepartExpense, setUpdateSparepartExpenseId } from '../../../actions/sparepart_expense';
import { getWarehouseOptions, getSparepartOptions } from '../../../actions/spare_part';
import { getFirmOptions, getUserOptions } from '../../../actions/user';
import { getTransportOptions } from '../../../actions/transport';
import { getMachineOptions } from '../../../actions/machine';
import { getExpenseTemplateOptions } from '../../../actions/expense_template';
import { usePermission } from '../../../hooks/usePermission';

export const SparepartExpenseUpdateForm = ({
    updateSparepartExpense,
    getSparepartExpense,
    setUpdateSparepartExpenseId,
    getWarehouseOptions,
    getSparepartOptions,
    getFirmOptions,
    getUserOptions,
    getTransportOptions,
    getMachineOptions,
    getExpenseTemplateOptions,
    spare_parts = [],
    warehouses = [],
    firms = [],
    users = [],
    transports = [],
    machines = [],
    expense_templates = [],
    user,
    expense,
    update_expense_id,
    loading,
    error
}) => {
    const { userCan } = usePermission();

    if (!userCan('edit sparepart expenses')) {
        return null;
    }

    // Check if user is director - only directors can see the "Ishlatuvchi" field
    const isDirector = user?.roles?.some(role => role.name === 'director');

    const [formData, setFormData] = useState({
        firm_id: '',
        spare_part_id: '',
        warehouse_id: '',
        transport_id: '',
        machine_id: '',
        expense_type: 'transport', // 'transport' or 'machine'
        quantity: '',
        notes: '',
        date: new Date().toISOString().split('T')[0],
        consumed_by: ''
    });

    const [errors, setErrors] = useState({});
    const [selectedFirm, setSelectedFirm] = useState(null);
    const [selectedSparePart, setSelectedSparePart] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedConsumedBy, setSelectedConsumedBy] = useState(null);
    const [selectedTransport, setSelectedTransport] = useState(null);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const initializedExpenseId = useRef(null);

    // Load initial data when component mounts
    useEffect(() => {
        getFirmOptions();
        getUserOptions();
        getTransportOptions();
        getMachineOptions();
        getExpenseTemplateOptions({ is_for_sparepart: true, is_all: true });
        // Don't load all warehouses initially - we'll load them when we have the firm
        // Also load all spare parts initially to show current stock
        getSparepartOptions();
    }, [getFirmOptions, getUserOptions, getTransportOptions, getMachineOptions, getExpenseTemplateOptions, getSparepartOptions]);

    // Load expense data when update_expense_id changes
    useEffect(() => {
        if (update_expense_id) {
            getSparepartExpense(update_expense_id);
        }
    }, [update_expense_id, getSparepartExpense]);

    // Populate editable fields once per opened expense. Option lists load
    // asynchronously; they must not reset a date (or any other field) that the
    // user has already changed while the modal is open.
    useEffect(() => {
        if (!update_expense_id) {
            initializedExpenseId.current = null;
            return;
        }

        const requestedExpenseLoaded = expense?.id
            && String(expense.id) === String(update_expense_id);

        if (requestedExpenseLoaded && String(initializedExpenseId.current) !== String(update_expense_id)) {
            setFormData({
                firm_id: expense.firm_id || '',
                spare_part_id: expense.spare_part_id || '',
                warehouse_id: expense.warehouse_id || '',
                transport_id: expense.transport_id || '',
                machine_id: expense.machine_id || '',
                expense_type: expense.expense_type || 'transport',
                quantity: expense.quantity || '',
                purpose: expense.purpose || '',
                notes: expense.notes || '',
                date: expense.date || new Date().toISOString().split('T')[0],
                consumed_by: expense.consumed_by || ''
            });

            initializedExpenseId.current = update_expense_id;
        }
    }, [expense, update_expense_id]);

    // Synchronize dropdown display values independently from editable form
    // data. These effects may rerun as option lists arrive without wiping edits.
    useEffect(() => {
        if (expense?.firm_id && firms.length > 0) {
            const firm = firms.find(f => f.id === expense.firm_id);
            if (firm) {
                setSelectedFirm({ value: firm.id, label: firm.name });
                getWarehouseOptions({ firm_id: firm.id, type: 'part' });
                getSparepartOptions({ firm_id: firm.id });
            }
        }
    }, [expense, firms, getWarehouseOptions, getSparepartOptions]);

    useEffect(() => {
        if (expense?.consumed_by && users.length > 0) {
            const selectedUser = users.find(u => u.id === expense.consumed_by);
            if (selectedUser) {
                setSelectedConsumedBy({ value: selectedUser.id, label: selectedUser.name });
            }
        }
    }, [expense, users]);

    useEffect(() => {
        if (expense?.transport_id && transports.length > 0) {
            const transport = transports.find(t => t.id === expense.transport_id);
            if (transport) {
                setSelectedTransport({ value: transport.id, label: transport.name });
            }
        }
    }, [expense, transports]);

    useEffect(() => {
        if (expense?.machine_id && machines.length > 0) {
            const machine = machines.find(m => m.id === expense.machine_id);
            if (machine) {
                setSelectedMachine({ value: machine.id, label: machine.name });
            }
        }
    }, [expense, machines]);

    // Set warehouse and spare part selections when data is loaded
    useEffect(() => {
        if (expense && expense.warehouse_id) {
            // Use expense data directly if warehouses array is empty
            if (warehouses.length > 0) {
                const warehouse = warehouses.find(w => w.id === expense.warehouse_id);
                if (warehouse) {
                    setSelectedWarehouse({ value: warehouse.id, label: warehouse.name });
                }
            } else if (expense.warehouse) {
                setSelectedWarehouse({ 
                    value: expense.warehouse.id, 
                    label: expense.warehouse.name 
                });
            }
        }
    }, [expense, warehouses]);

    useEffect(() => {
        if (expense && expense.spare_part_id) {
            // Use expense data directly if spare_parts array is empty
            if (spare_parts.length > 0) {
                const sparePart = spare_parts.find(p => p.id === expense.spare_part_id);
                if (sparePart) {
                    setSelectedSparePart({
                        value: sparePart.id,
                        label: `${sparePart.name} (Mavjud: ${sparePart.in_stock || 0} ${sparePart.unit?.name || ''}) - ${sparePart.warehouse?.name}`,
                        data: sparePart
                    });
                }
            } else if (expense.spare_part) {
                setSelectedSparePart({
                    value: expense.spare_part.id,
                    label: `${expense.spare_part.name} (Mavjud: ${expense.spare_part.in_stock || 0} ${expense.spare_part.unit || ''}) - ${expense.warehouse?.name || ''}`,
                    data: expense.spare_part
                });
            }
        }
    }, [expense, spare_parts]);

    // Filter spare parts when firm changes (only for create form, not needed for update)
    // useEffect(() => {
    //     // Only load spare parts when firm is selected
    //     if (selectedFirm?.value) {
    //         getSparepartOptions({
    //             firm_id: selectedFirm.value
    //         });
    //     }
    // }, [selectedFirm, getSparepartOptions]);

    // Prepare options for selects
    const firmOptions = Array.isArray(firms) ? firms.map(firm => ({
        value: firm.id,
        label: firm.name
    })) : [];

    // Create spare part options including the expense's spare part if not in the array
    const sparePartOptions = Array.isArray(spare_parts) ? spare_parts.map(part => ({
        value: part.id,
        label: `${part.name} (Mavjud: ${part.in_stock || 0} ${part.unit?.name || ''}) - ${part.warehouse?.name}`,
        data: part
    })) : [];
    
    // Add expense spare part if it's not in the array
    if (expense?.spare_part && !sparePartOptions.find(p => p.value === expense.spare_part.id)) {
        sparePartOptions.push({
            value: expense.spare_part.id,
            label: `${expense.spare_part.name} (Mavjud: ${expense.spare_part.in_stock || 0} ${expense.spare_part.unit || ''}) - ${expense.warehouse?.name || ''}`,
            data: expense.spare_part
        });
    }

    // Filter warehouses by selected firm (for update form, we'll use all warehouses since firm is disabled)
    // Create warehouse options including the expense's warehouse if not in the array
    const warehouseOptions = Array.isArray(warehouses) ? warehouses.map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
    })) : [];
    
    // Add expense warehouse if it's not in the array
    if (expense?.warehouse && !warehouseOptions.find(w => w.value === expense.warehouse.id)) {
        warehouseOptions.push({
            value: expense.warehouse.id,
            label: expense.warehouse.name
        });
    }

    const userOptions = Array.isArray(users) ? users.map(user => ({
        value: user.id,
        label: user.name
    })) : [];

    const transportOptions = Array.isArray(transports) ? transports.map(transport => ({
        value: transport.id,
        label: `${transport.name}${transport.plate ? ` - ${transport.plate}` : ''}`
    })) : [];

    const machineOptions = Array.isArray(machines) ? machines.map(machine => ({
        value: machine.id,
        label: machine.name
    })) : [];



    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleFirmChange = (selectedOption) => {
        setSelectedFirm(selectedOption);
        setSelectedSparePart(null); // Reset spare part when firm changes
        setSelectedWarehouse(null); // Reset warehouse when firm changes
        
        setFormData(prev => ({
            ...prev,
            firm_id: selectedOption?.value || '',
            spare_part_id: '',
            warehouse_id: ''
        }));
        
        // Refetch warehouses with the selected firm_id
        if (selectedOption && selectedOption.value) {
            getWarehouseOptions({ firm_id: selectedOption.value, type: 'part' });
        } else {
            // If no firm is selected, get all spare part warehouses
            getWarehouseOptions({ type: 'part' });
        }
    };

    const handleSparePartChange = (selectedOption) => {
        setSelectedSparePart(selectedOption);
        setFormData(prev => ({
            ...prev,
            spare_part_id: selectedOption?.value || ''
        }));
        
        // Auto-select the warehouse where this spare part is located
        if (selectedOption?.data.warehouse_id) {
            const warehouseOption = warehouseOptions.find(w => w.value === selectedOption.data.warehouse_id);
            setSelectedWarehouse(warehouseOption);
            setFormData(prev => ({
                ...prev,
                warehouse_id: selectedOption.data.warehouse_id
            }));
        } else {
            setSelectedWarehouse(null);
            setFormData(prev => ({
                ...prev,
                warehouse_id: ''
            }));
        }
    };

    const handleWarehouseChange = (selectedOption) => {
        setSelectedWarehouse(selectedOption);
        setFormData(prev => ({
            ...prev,
            warehouse_id: selectedOption?.value || ''
        }));
    };

    const handleConsumedByChange = (selectedOption) => {
        setSelectedConsumedBy(selectedOption);
        setFormData(prev => ({
            ...prev,
            consumed_by: selectedOption?.value || ''
        }));
    };

    const handleExpenseTypeChange = (e) => {
        const expenseType = e.target.value;
        setFormData(prev => ({
            ...prev,
            expense_type: expenseType,
            transport_id: '',
            machine_id: ''
        }));
        setSelectedTransport(null);
        setSelectedMachine(null);
    };

    const handleTransportChange = (selectedOption) => {
        setSelectedTransport(selectedOption);
        setFormData(prev => ({
            ...prev,
            transport_id: selectedOption?.value || ''
        }));
    };

    const handleMachineChange = (selectedOption) => {
        setSelectedMachine(selectedOption);
        setFormData(prev => ({
            ...prev,
            machine_id: selectedOption?.value || ''
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.firm_id) newErrors.firm_id = 'Firma tanlash majburiy';
        if (!formData.spare_part_id) newErrors.spare_part_id = 'Ehtiyot qism tanlash majburiy';
        if (!formData.warehouse_id) newErrors.warehouse_id = 'Ombor tanlash majburiy';
        if (!formData.quantity || formData.quantity <= 0) newErrors.quantity = 'Miqdor kiritish majburiy va 0 dan katta bo\'lishi kerak';
        if (isDirector && !formData.consumed_by) newErrors.consumed_by = 'Ishlatuvchi tanlash majburiy';
        if (!formData.date) {
            newErrors.date = 'Sana tanlash majburiy';
        } else if (!isDirector) {
            // Check if date is within last 2 days for non-directors
            const selectedDate = new Date(formData.date);
            const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
            const today = new Date();
            
            if (selectedDate < twoDaysAgo || selectedDate > today) {
                newErrors.date = 'Faqat oxirgi 2 kun ichidagi sanalarni tanlashingiz mumkin';
            }
        }
        
        // Validate transport or machine based on expense type
        if (formData.expense_type === 'transport' && !formData.transport_id) {
            newErrors.transport_id = 'Transport tanlash majburiy';
        }
        if (formData.expense_type === 'machine' && !formData.machine_id) {
            newErrors.machine_id = 'Uskuna tanlash majburiy';
        }
        
        // Check if there's enough stock (for updates, we need to consider the current expense quantity)
        if (selectedSparePart && formData.quantity > 0) {
            const currentStock = selectedSparePart.data.in_stock;
            const currentExpenseQuantity = expense?.quantity || 0;
            const newQuantity = parseFloat(formData.quantity);
            const quantityDifference = newQuantity - currentExpenseQuantity;
            
            // Calculate available quantity including the current expense quantity that could be freed up
            const availableQuantity = currentStock + currentExpenseQuantity;
            
            if (newQuantity > availableQuantity) {
                newErrors.quantity = `Omborida yetarli miqdor yo'q. Maksimal: ${availableQuantity}`;
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        // Prepare form data - if user is not director, set consumed_by to current user's ID
        const submissionData = { ...formData };
        if (!isDirector) {
            submissionData.consumed_by = user?.id || '';
        }

        const success = await updateSparepartExpense(update_expense_id, submissionData);
        if (success) {
            handleClose();
        }
    };

    const handleClose = () => {
        setUpdateSparepartExpenseId(null);
        setFormData({
            firm_id: '',
            spare_part_id: '',
            warehouse_id: '',
            transport_id: '',
            machine_id: '',
            expense_type: 'transport',
            quantity: '',
            purpose: '',
            notes: '',
            date: new Date().toISOString().split('T')[0],
            consumed_by: ''
        });
        setSelectedFirm(null);
        setSelectedSparePart(null);
        setSelectedWarehouse(null);
        setSelectedConsumedBy(null);
        setSelectedTransport(null);
        setSelectedMachine(null);
        setErrors({});
    };

    return (
        <Modal show={!!update_expense_id} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Ehtiyot qism sarfini tahrirlash</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger">
                            {error.message || 'Xatolik yuz berdi'}
                        </Alert>
                    )}

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Firma <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={selectedFirm}
                                    onChange={handleFirmChange}
                                    options={firmOptions}
                                    placeholder="Firma tanlang..."
                                    isClearable
                                    isSearchable
                                    isDisabled={true}
                                />
                                {errors.firm_id && <div className="text-danger small">{errors.firm_id}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ombor <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={selectedWarehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouseOptions}
                                    placeholder={selectedFirm ? "Ombor tanlang..." : "Avval firma tanlang"}
                                    isClearable
                                    isSearchable
                                    isDisabled={!selectedFirm}
                                />
                                {errors.warehouse_id && <div className="text-danger small">{errors.warehouse_id}</div>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ehtiyot qism <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={selectedSparePart}
                                    onChange={handleSparePartChange}
                                    options={sparePartOptions}
                                    placeholder={selectedFirm ? "Ehtiyot qism tanlang..." : "Avval firma tanlang"}
                                    isClearable
                                    isSearchable
                                    isDisabled={!selectedFirm}
                                />
                                {errors.spare_part_id && <div className="text-danger small">{errors.spare_part_id}</div>}
                                {!selectedFirm && <div className="text-muted small">Ehtiyot qism tanlash uchun avval firma tanlang</div>}
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Expense Type Radio Buttons */}
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sarflash turi <span className="text-danger">*</span></Form.Label>
                                <div className="d-flex gap-4 mt-2">
                                    <Form.Check
                                        type="radio"
                                        id="expense-type-transport"
                                        name="expense_type"
                                        value="transport"
                                        checked={formData.expense_type === 'transport'}
                                        onChange={handleExpenseTypeChange}
                                        label="Transport uchun"
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="expense-type-machine"
                                        name="expense_type"
                                        value="machine"
                                        checked={formData.expense_type === 'machine'}
                                        onChange={handleExpenseTypeChange}
                                        label="Uskuna uchun"
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Transport/Machine Selection */}
                    <Row>
                        <Col md={12}>
                            {formData.expense_type === 'transport' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Transport <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        value={selectedTransport}
                                        onChange={handleTransportChange}
                                        options={transportOptions}
                                        placeholder="Transport tanlang..."
                                        isClearable
                                        isSearchable
                                    />
                                    {errors.transport_id && <div className="text-danger small">{errors.transport_id}</div>}
                                </Form.Group>
                            )}
                            
                            {formData.expense_type === 'machine' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Uskuna <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        value={selectedMachine}
                                        onChange={handleMachineChange}
                                        options={machineOptions}
                                        placeholder="Uskuna tanlang..."
                                        isClearable
                                        isSearchable
                                    />
                                    {errors.machine_id && <div className="text-danger small">{errors.machine_id}</div>}
                                </Form.Group>
                            )}
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Miqdor <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="number"
                                    step="1"
                                    name="quantity"
                                    value={formData.quantity}
                                    onChange={handleInputChange}
                                    placeholder="Miqdorni kiriting"
                                />
                                {errors.quantity && <div className="text-danger small">{errors.quantity}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sana <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    max={new Date().toISOString().split('T')[0]}
                                    min={isDirector ? undefined : new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                />
                                {errors.date && <div className="text-danger small">{errors.date}</div>}
                                {!isDirector && (
                                    <small className="text-muted">
                                        Faqat oxirgi 2 kun ichidagi sanalarni tanlashingiz mumkin
                                    </small>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        {isDirector && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ishlatuvchi <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        value={selectedConsumedBy}
                                        onChange={handleConsumedByChange}
                                        options={userOptions}
                                        placeholder="Ishlatuvchini tanlang..."
                                        isClearable
                                        isSearchable
                                    />
                                    {errors.consumed_by && <div className="text-danger small">{errors.consumed_by}</div>}
                                </Form.Group>
                            </Col>
                        )}
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Izohlar</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Qo'shimcha izohlar..."
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {selectedSparePart && (
                        <Alert variant="info">
                            <strong>Tanlangan ehtiyot qism:</strong> {selectedSparePart.data.name}<br/>
                            <strong>Hozirgi ombordagi miqdor:</strong> {selectedSparePart.data.in_stock} {selectedSparePart.data.unit?.name || ''}<br/>
                            <strong>Hozirgi sarflangan miqdor:</strong> {expense?.quantity || 0} {selectedSparePart.data.unit?.name || ''}<br/>
                            <strong>Maksimal yangi miqdor:</strong> {(selectedSparePart.data.in_stock + (expense?.quantity || 0))} {selectedSparePart.data.unit?.name || ''}<br/>
                            <strong>Ombor:</strong> {selectedSparePart.data.warehouse?.name}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Bekor qilish
                    </Button>
                    <Button 
                        variant="primary" 
                        type="submit" 
                        disabled={loading}
                    >
                        {loading ? 'Yangilanmoqda...' : 'O\'zgarishlarni saqlash'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

SparepartExpenseUpdateForm.propTypes = {
    updateSparepartExpense: PropTypes.func.isRequired,
    getSparepartExpense: PropTypes.func.isRequired,
    setUpdateSparepartExpenseId: PropTypes.func.isRequired,
    getWarehouseOptions: PropTypes.func.isRequired,
    getSparepartOptions: PropTypes.func.isRequired,
    getFirmOptions: PropTypes.func.isRequired,
    getUserOptions: PropTypes.func.isRequired,
    getTransportOptions: PropTypes.func.isRequired,
    getMachineOptions: PropTypes.func.isRequired,
    getExpenseTemplateOptions: PropTypes.func.isRequired,
    spare_parts: PropTypes.array,
    warehouses: PropTypes.array,
    firms: PropTypes.array,
    users: PropTypes.array,
    transports: PropTypes.array,
    machines: PropTypes.array,
    expense_templates: PropTypes.array,
    user: PropTypes.object,
    expense: PropTypes.object,
    update_expense_id: PropTypes.number,
    loading: PropTypes.bool,
    error: PropTypes.object
};

const mapStateToProps = state => ({
    spare_parts: state.spare_part.spare_part_options,
    warehouses: state.spare_part.warehouse_options,
    firms: state.user.firm_options,
    users: state.user.user_options,
    transports: state.transport.transport_options,
    machines: state.machine.machine_options,
    expense_templates: state.expense_template.expense_template_options,
    user: state.auth.user,
    expense: state.sparepart_expense.expense,
    update_expense_id: state.sparepart_expense.update_expense_id,
    loading: state.sparepart_expense.loading,
    error: state.sparepart_expense.error
});

export default connect(mapStateToProps, { 
    updateSparepartExpense,
    getSparepartExpense,
    setUpdateSparepartExpenseId,
    getWarehouseOptions,
    getSparepartOptions,
    getFirmOptions,
    getUserOptions,
    getTransportOptions,
    getMachineOptions,
    getExpenseTemplateOptions
})(SparepartExpenseUpdateForm);
